import { merge, uniq } from 'lodash-es';
import { normalize } from 'normalizr';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import {
  SUGGESTERS_SCHEMA_KEY,
  SUGGESTION_SCHEMA_KEY,
  suggestionListSchema,
} from 'schema/suggestions';
import type { Suggestion } from 'types/suggestion';
import type { User } from 'types/user';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import type { State } from './suggestions.types';
import * as actionTypes from './suggestions.types';

export const initialState: State = {
  xhrStatus: xhrStatusFactory(),
  entities: {
    [SUGGESTION_SCHEMA_KEY]: {},
    [SUGGESTERS_SCHEMA_KEY]: {},
  },
  result: [],
};

const reducer = reducerWithInitialState<State>(initialState)
  .case(actionTypes.fetchedSuggestions, (state, suggestions) => {
    const normalizedSuggestions = normalize<
      Suggestion,
      actionTypes.NormalizedPayload['entities'],
      actionTypes.NormalizedPayload['result']
    >(suggestions, suggestionListSchema);
    return {
      ...state,
      ...normalizedSuggestions,
    };
  })
  .case(actionTypes.appendSuggestion, (state, payload) => {
    const normalized = normalize<
      Suggestion<User>,
      actionTypes.NormalizedPayload['entities'],
      actionTypes.NormalizedPayload['result']
    >(payload, suggestionListSchema);

    if (Object.keys(normalized.entities).length === 0) {
      return state;
    }

    const suggesters = merge({}, state.entities.suggesters, normalized.entities.suggesters);
    const oldSuggestions = state.result.reduce((obj: Record<number, Suggestion>, key) => {
      if (normalized.entities.suggestions[key] == null) {
        obj[key] = state.entities.suggestions[key];
      }
      return obj;
    }, {});
    const suggestions = merge({}, oldSuggestions, normalized.entities.suggestions);
    const result = uniq([...state.result, ...normalized.result]);

    return {
      ...state,
      entities: { suggesters, suggestions },
      result,
    };
  })
  .case(actionTypes.setSuggestionEntries, (state, payload) => {
    // Don't attempt to update synthetic suggestions
    if (payload.suggestionId < 0) return state;

    const suggestion = state.entities.suggestions[payload.suggestionId];
    if (suggestion == null) {
      return state;
    }
    const updatedSuggestion: Suggestion = {
      ...suggestion,
      suggested_entry_ids: payload.entryIds,
    };
    return {
      ...state,
      entities: {
        ...state.entities,
        suggestions: {
          ...state.entities.suggestions,
          [payload.suggestionId]: updatedSuggestion,
        },
      },
    };
  })
  .cases([actionTypes.acceptSuggestionId, actionTypes.dismissSuggestionId], (state, payload) => {
    return {
      ...state,
      result: state.result.filter(id => id !== payload),
    };
  })
  .case(actionTypes.reAppendSuggestion, (state, suggestion) => {
    return {
      ...state,
      entities: {
        ...state.entities,
        [suggestion.id]: suggestion,
      },
      result: uniq([...state.result, suggestion.id]),
    };
  });

export default actionTypes.withXhrStatusReducer(reducer);
